import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Plus, SquareArrowOutUpRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  type ResourceSet,
  useResourceSets,
} from 'components/IAP/hooks/resourceSetHooks';
import {
  catalogResourceForInput,
  resourceInputText,
} from 'components/IAP/resourceCatalogName';
import { DhsResourceDetailsDialog } from 'components/MapPage/CorporateViewTabs/ResourceTab/DhsResourceDetailsDialog';
import { resourceSetToRow } from 'components/MapPage/CorporateViewTabs/ResourceTab/resourceSetToRow';
import type { ResourceRow } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourceTabCard';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';
import { TaskAssetRequestDialog } from './TaskAssetRequestDialog';
import { TicketSection } from './TicketSection';
import {
  addRow,
  EMPTY_REQUIREMENT,
  removeRow,
  totalNeed,
  updateRequirement,
  type ActionResourceRequirement,
} from './actionTicketModel';

type ResourceRequirementsSectionProps = {
  value: ActionResourceRequirement[];
  onChange: (rows: ActionResourceRequirement[]) => void;
  notApplicable?: boolean;
  onNotApplicableChange?: (next: boolean) => void;
  /** Roster seat the task was opened from. */
  positionName?: string;
};

const DOCUMENTATION_ENTRY = '__documentation_entry__';

function catalogId(resource: ResourceSet): string {
  return resource.id || resource.resource_set_id || '';
}

/**
 * Typed names are documentation entries. A name that matches the org catalog is
 * stored as that resource instead.
 */
function AssetNameField({
  name,
  resourceSetId,
  onCommit,
  label,
}: {
  name: string;
  resourceSetId?: string | null;
  onCommit: (next: {
    resourceName: string;
    resourceSetId: string | null;
  }) => void;
  label: string;
}) {
  const groupId = useSelector(selectGroupGuid);
  const { data: resourceSets = [], isLoading } = useResourceSets();
  const { data: groupMembers = [] } = useGetRostersForGroup(groupId);
  const [query, setQuery] = useState(name);
  const [detailsRow, setDetailsRow] = useState<ResourceRow | null>(null);
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    setQuery(resourceInputText(name, resourceSets));
  }, [name, resourceSets]);

  useEffect(() => {
    if (isLoading) return;
    const picked = catalogResourceForInput(name, resourceSets);
    if (!picked || picked.name === name) return;
    onCommit({ resourceName: picked.name, resourceSetId: picked.id });
    setQuery(picked.name);
  }, [isLoading, name, onCommit, resourceSets]);

  const trimmed = query.trim();
  const matches = useMemo(() => {
    const needle = trimmed.toLowerCase();
    const rows = needle
      ? resourceSets.filter((resource) =>
          resource.name.toLowerCase().includes(needle)
        )
      : resourceSets;
    return rows.slice(0, 50);
  }, [resourceSets, trimmed]);
  const exact = resourceSets.find(
    (resource) => resource.name.trim().toLowerCase() === trimmed.toLowerCase()
  );
  const linkedResource = useMemo(() => {
    if (!resourceSetId) return undefined;
    return resourceSets.find(
      (resource) =>
        resource.id === resourceSetId ||
        resource.resource_set_id === resourceSetId
    );
  }, [resourceSetId, resourceSets]);

  const commitText = (text: string) => {
    const next = text.trim();
    const match =
      catalogResourceForInput(next, resourceSets) ||
      resourceSets.find(
        (resource) => resource.name.trim().toLowerCase() === next.toLowerCase()
      );
    if (match) {
      onCommit({ resourceName: match.name, resourceSetId: match.id });
      setQuery(match.name);
      return;
    }
    onCommit({ resourceName: next, resourceSetId: null });
    setQuery(next);
  };

  const openDetails = (resource: ResourceSet) => {
    setDialogMode('view');
    setDetailsRow(resourceSetToRow(resource, groupMembers));
  };

  const viewDetailsButton = (resource: ResourceSet, compact = false) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={
        compact
          ? 'tw:h-9 tw:shrink-0 tw:px-2 tw:text-xs'
          : 'tw:h-7 tw:shrink-0 tw:px-2 tw:text-xs'
      }
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openDetails(resource);
      }}
    >
      View Details
      <SquareArrowOutUpRight className="tw:size-3.5" />
    </Button>
  );

  return (
    <>
      <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-1">
        <Combobox
          value={resourceSetId || null}
          filter={null}
          inputValue={query}
          onInputValueChange={(next) =>
            setQuery(resourceInputText(String(next ?? ''), resourceSets))
          }
          onValueChange={(value) => {
            const pickedId = typeof value === 'string' ? value : '';
            if (!pickedId || pickedId === DOCUMENTATION_ENTRY) {
              commitText(query);
              return;
            }
            const picked = resourceSets.find(
              (resource) => catalogId(resource) === pickedId
            );
            if (!picked) return;
            onCommit({ resourceName: picked.name, resourceSetId: picked.id });
            setQuery(picked.name);
          }}
        >
          <ComboboxInput
            aria-label={label}
            placeholder="Type a documentation entry or pick a resource"
            className="tw:w-full"
            onBlur={() => {
              if (isLoading) return;
              if (trimmed === name.trim() && !!resourceSetId === !!exact) return;
              commitText(query);
            }}
          />
          <ComboboxContent className="dark" positionerClassName="tw:z-[60]">
            <ComboboxEmpty>No organization resources</ComboboxEmpty>
            <ComboboxList>
              {trimmed && !exact ? (
                <ComboboxItem value={DOCUMENTATION_ENTRY}>
                  Documentation entry: {trimmed}
                </ComboboxItem>
              ) : null}
              {matches.map((resource) => {
                const id = catalogId(resource);
                return (
                  <ComboboxItem
                    key={id}
                    value={id}
                    className="tw:flex tw:items-center tw:justify-between tw:gap-2"
                  >
                    <span className="tw:min-w-0 tw:truncate">
                      {resource.name}
                    </span>
                    {viewDetailsButton(resource)}
                  </ComboboxItem>
                );
              })}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        {linkedResource ? viewDetailsButton(linkedResource, true) : null}
      </div>
      <DhsResourceDetailsDialog
        row={detailsRow}
        open={!!detailsRow}
        onOpenChange={(open) => {
          if (!open) setDetailsRow(null);
        }}
        mode={dialogMode}
        onModeChange={setDialogMode}
        openedInEdit={false}
        groupMembers={groupMembers}
        onRowPatch={(patch) => {
          setDetailsRow((prev) => (prev ? { ...prev, ...patch } : prev));
        }}
      />
    </>
  );
}

/**
 * The ICS-215 style Required / Have / Need triplet, carried onto a task so a
 * resource ask travels with the work rather than only on a work assignment.
 * `Need` is read-only because it is derived on both sides of the wire.
 */
export const ResourceRequirementsSection: React.FC<
  ResourceRequirementsSectionProps
> = ({
  value,
  onChange,
  notApplicable = false,
  onNotApplicableChange,
  positionName,
}) => {
  const hwcg = useHwcgFlag();
  const showNa = hwcg && !!onNotApplicableChange;
  const hideRows = showNa && notApplicable;
  const [showCounts, setShowCounts] = useState(false);
  const [requestIndex, setRequestIndex] = useState<number | null>(null);
  const countsVisible = !hwcg || showCounts;
  const nameLabel = hwcg ? 'Asset' : 'Resource';
  const gridClass = hwcg
    ? countsVisible
      ? 'tw:grid-cols-[minmax(0,1fr)_auto] tw:sm:grid-cols-[minmax(0,1fr)_auto_4.5rem_4.5rem_4.5rem_2rem]'
      : 'tw:grid-cols-[minmax(0,1fr)_auto_2rem]'
    : countsVisible
      ? 'tw:grid-cols-2 tw:sm:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_4.5rem_2rem]'
      : 'tw:grid-cols-[minmax(0,1fr)_2rem]';
  const requestRow =
    requestIndex == null ? null : (value[requestIndex] ?? null);

  return (
    <TicketSection
      title={hwcg ? 'Assets Requested' : 'Resource Requirements'}
      count={
        hideRows
          ? 'N/A'
          : value.length
            ? countsVisible
              ? `${totalNeed(value)} needed`
              : String(value.length)
            : undefined
      }
      actions={
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {hwcg ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Switch
                id="show-required-have-need"
                checked={showCounts}
                onCheckedChange={(checked) => setShowCounts(checked === true)}
              />
              <Label
                htmlFor="show-required-have-need"
                className="tw:font-normal"
              >
                Show Required Have Need
              </Label>
            </div>
          ) : null}
          {showNa ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id="resource-requirements-na"
                checked={notApplicable}
                onCheckedChange={(checked) => {
                  const next = checked === true;
                  onNotApplicableChange(next);
                  if (next) onChange([]);
                }}
              />
              <Label
                htmlFor="resource-requirements-na"
                className="tw:font-normal"
              >
                N/A
              </Label>
            </div>
          ) : null}
          {hideRows ? null : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange(addRow(value, { ...EMPTY_REQUIREMENT }))}
            >
              <Plus /> {hwcg ? 'Add Asset' : 'Add Requirement'}
            </Button>
          )}
        </div>
      }
    >
      {hideRows ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          Marked not applicable.
        </p>
      ) : value.length === 0 ? (
        <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
          {hwcg ? 'No assets requested yet.' : 'No resource requirements yet.'}
        </p>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div
            className={`tw:hidden tw:gap-2 tw:text-xs tw:text-muted-foreground tw:sm:grid ${gridClass}`}
          >
            <span>{nameLabel}</span>
            {hwcg ? <span>Linked 213RR</span> : null}
            {countsVisible ? (
              <>
                <span>Required</span>
                <span>Have</span>
                <span>Need</span>
              </>
            ) : null}
            <span className="tw:sr-only">Remove</span>
          </div>
          {value.map((row, index) => (
            <div
              // Rows are positional (no server id until save), so the index is
              // the only stable identity here.
              key={index}
              className={`tw:grid tw:items-center tw:gap-2 ${gridClass}`}
            >
              {hwcg ? (
                <AssetNameField
                  name={row.resourceName}
                  resourceSetId={row.resourceSetId}
                  label={`Asset ${index + 1}`}
                  onCommit={(next) =>
                    onChange(updateRequirement(value, index, next))
                  }
                />
              ) : (
                <Input
                  className="tw:col-span-2 tw:sm:col-span-1"
                  value={row.resourceName}
                  placeholder="e.g. Ambulance, Type 1"
                  aria-label={`Resource ${index + 1}`}
                  onChange={(e) =>
                    onChange(
                      updateRequirement(value, index, {
                        resourceName: e.target.value,
                      })
                    )
                  }
                />
              )}
              {hwcg ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRequestIndex(index)}
                >
                  Linked 213RR
                </Button>
              ) : null}
              {countsVisible ? (
                <>
                  <Input
                    type="number"
                    min={0}
                    value={row.required}
                    aria-label={`Required for resource ${index + 1}`}
                    onChange={(e) =>
                      onChange(
                        updateRequirement(value, index, {
                          required: Number(e.target.value),
                        })
                      )
                    }
                  />
                  <Input
                    type="number"
                    min={0}
                    value={row.have}
                    aria-label={`Have for resource ${index + 1}`}
                    onChange={(e) =>
                      onChange(
                        updateRequirement(value, index, {
                          have: Number(e.target.value),
                        })
                      )
                    }
                  />
                  <output
                    className="tw:flex tw:h-9 tw:items-center tw:px-3 tw:text-sm tw:font-medium"
                    aria-label={`Need for resource ${index + 1}`}
                  >
                    {row.need}
                  </output>
                </>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${nameLabel.toLowerCase()} ${index + 1}`}
                onClick={() => onChange(removeRow(value, index))}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
      )}
      {requestRow ? (
        <TaskAssetRequestDialog
          open
          onOpenChange={(next) => {
            if (!next) setRequestIndex(null);
          }}
          assetName={requestRow.resourceName}
          resourceSetId={requestRow.resourceSetId}
          resourceRequestId={requestRow.resourceRequestId}
          positionName={positionName}
          onLinked={(resourceRequestId) => {
            if (requestIndex == null) return;
            onChange(
              updateRequirement(value, requestIndex, { resourceRequestId })
            );
          }}
        />
      ) : null}
    </TicketSection>
  );
};
